import { ThumbsUp } from "@phosphor-icons/react";

import FilterButton from "common/components/ui/FilterButton";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectItemText,
  SelectTrigger,
  SelectValue,
} from "common/components/ui/Select";
import Switch from "common/components/ui/Switch";
import { typedObjectEntries } from "common/helpers/utils";
import useTwBreakpoint from "common/hooks/useTwBreakpoint";

import { REACTIONS } from "../../../constants";
import ActionDelay from "./ActionDelay";
import SectionHeading from "./SectionHeading";

import type { ReactionType } from "../../../types";

export default function ReactToPost({
  settings: { isEnabled, reactionType, delay },
  updateSettings,
  isDisabled,
  errorMessage,
}: {
  settings: { isEnabled: boolean; reactionType: ReactionType; delay: number };
  updateSettings: (
    isEnabled: boolean,
    reactionType: ReactionType,
    delay: number,
  ) => void;
  isDisabled: boolean;
  errorMessage: string;
}) {
  const isDesktop = useTwBreakpoint("lg");

  const SelectionIcon = REACTIONS[reactionType].icon;

  return (
    <div className="flex flex-col gap-y-2 lg:gap-y-3">
      <div className="flex flex-row justify-between">
        <SectionHeading
          title="React to a post"
          description="Engage your target by reacting to their latest post"
          icon={<ThumbsUp weight="fill" />}
          shouldShowMobileTooltip
        />
        <div className="flex flex-row items-center gap-3">
          {isEnabled && (
            <Select
              value={reactionType}
              onValueChange={(value: ReactionType) =>
                updateSettings(isEnabled, value, delay)
              }
              disabled={isDisabled}
            >
              <SelectTrigger className="lg:w-40" asChild>
                <FilterButton leftIcon={<SelectionIcon />}>
                  <SelectValue />
                </FilterButton>
              </SelectTrigger>
              <SelectContent className="max-h-64">
                {typedObjectEntries(REACTIONS).map(
                  ([key, { icon: Icon, label }]) => (
                    <SelectItem value={key} key={label}>
                      <Icon className="size-5" />
                      <SelectItemText>{label}</SelectItemText>
                    </SelectItem>
                  ),
                )}
              </SelectContent>
            </Select>
          )}
          <Switch
            checked={isEnabled}
            onCheckedChange={(checked) => {
              updateSettings(checked, reactionType, delay || 86400000);
            }}
            disabled={isDisabled}
          />
        </div>
      </div>
      {isEnabled && (
        <ActionDelay
          delay={delay}
          onDelayChange={(newDelay) => {
            updateSettings(isEnabled, reactionType, newDelay);
          }}
          isDisabled={isDisabled}
          labels={{
            action: isDesktop ? "React to a post" : "your reaction",
            after: "reaching out",
          }}
          errorMessage={errorMessage}
        />
      )}
    </div>
  );
}
